// OP-3605 — Hooks SWR do módulo Estoque. URL como chave; retorno
// { rows, total, page, lastPage, isLoading, isError, isUnauthorized, mutate }.
import useSWR from 'swr'
import { APIDecoratorWithBaseURI } from '../../Service/api'
import { urlEstoque } from '../../Service/estoque'
import {
  CatalogoItemRow, EstoqueCliente, EstoqueLocal, EventoApto, FilaSolicitacaoRow, MinhaSolicitacaoDetalhe,
  MinhaSolicitacaoRow, MovimentoRow, MovimentosFilters, Paginado, PosicaoFilters, PosicaoRow,
  SolicitacoesFilters, TecnicoOption, SolicitacaoDetalhe, RemessaRow, DisponivelRow, VinculoResolvido,
  RemessaDetalhe, RemessasFilters, VinculoRow, VinculoDetalhe, VinculosFilters, AnexoSalvo,
  UnidadeRow, UnidadeDetalhe, HistoricoUnidadeRow, UsoUnidade, EquipamentoRow, EquipamentosFilters,
  TransferenciaRow, TransferenciaDetalhe, TransferenciasFilters,
  InventarioRow, InventarioDetalhe, InventariosFilters, BaixaRow, BaixasFilters, BaixaSemPropostaRow,
  ItemCatalogoRow, ItemCatalogoDetalhe, CatalogoFilters, Ocorrencia, NaturezaOcorrencia, CategoriaRow, OndeTemRow, EstoqueHoje,
  ConfigNotificacao,
} from '../../types/estoque'
import { lerLista, lerPaginado } from './adaptadores'
import { useDebounce } from '../useDebounce'

const SEM_REVALIDAR = { revalidateOnFocus: false, refreshInterval: 0 }

function useLista<T>(url: string | null, ler: (body: any) => Paginado<T>) {
  const { data, error, isLoading, mutate } = useSWR(url, () => APIDecoratorWithBaseURI().get(url as string), SEM_REVALIDAR)
  const p = ler(data?.data)
  return {
    ...p,
    isLoading,
    isError: error,
    // Sessão vencida no backend: quem navega é a página (padrão do OS, OP-3462).
    isUnauthorized: (error as any)?.response?.status === 401,
    mutate,
  }
}

export const useEstoqueLocais = () => {
  const url = urlEstoque('getEstoqueLocais')
  const { data, error, isLoading, mutate } = useSWR(url, () => APIDecoratorWithBaseURI().get(url), SEM_REVALIDAR)
  return {
    locais: (data?.data?.data ?? []) as EstoqueLocal[],
    isLoading,
    isError: error,
    isUnauthorized: (error as any)?.response?.status === 401,
    mutate,
  }
}

/**
 * O resumo do dia (ESL1): contadores já no escopo de praça de quem está logado.
 * É a única leitura que revalida ao voltar o foco: quem deixa a aba aberta
 * o dia inteiro precisa ver a fila andar.
 */
export const useEstoqueHoje = () => {
  const url = urlEstoque('getEstoqueHoje')
  const { data, error, isLoading, mutate } = useSWR(url, () => APIDecoratorWithBaseURI().get(url), { revalidateOnFocus: true, refreshInterval: 0 })
  return {
    data: (data?.data?.data ?? null) as EstoqueHoje | null,
    isLoading,
    isError: error,
    isUnauthorized: (error as any)?.response?.status === 401,
    mutate,
  }
}

/** Posição de um local; sem `localId` não consulta (chave nula). */
export const useEstoquePosicao = (f: PosicaoFilters) =>
  useLista<PosicaoRow>(f.localId ? urlEstoque('getEstoquePosicao', f) : null, lerPaginado)

export const useEstoqueMovimentos = (f: MovimentosFilters) =>
  useLista<MovimentoRow>(urlEstoque('getEstoqueMovimentos', f), lerPaginado)

/** Fila da logística (ESL1): paginada com total. */
export const useEstoqueSolicitacoes = (f: SolicitacoesFilters) =>
  useLista<FilaSolicitacaoRow>(urlEstoque('getEstoqueSolicitacoes', f), lerPaginado)

/** Meus pedidos (ESL2): o backend devolve a lista já cortada em 50, sem total. */
export const useEstoqueMinhasSolicitacoes = (page = 1) =>
  useLista<MinhaSolicitacaoRow>(urlEstoque('getEstoqueMinhasSolicitacoes', { page }), (b) => lerLista(b, page))

// ---- Apoio ao formulário e detalhe do solicitante ----
function useSimples<T>(url: string | null, vazio: T) {
  const { data, error, isLoading, mutate } = useSWR(url, () => APIDecoratorWithBaseURI().get(url as string), SEM_REVALIDAR)
  return {
    data: (data?.data?.data ?? vazio) as T,
    isLoading,
    isError: error,
    isUnauthorized: (error as any)?.response?.status === 401,
    mutate,
  }
}

/** A busca espera 300ms parada; `termo` é o que produziu a lista. */
export const useEstoqueClientes = (busca: string) => {
  const termo = useDebounce(busca.trim(), 300)
  return { ...useSimples<EstoqueCliente[]>(urlEstoque('getEstoqueClientes', { busca: termo || undefined }), []), termo }
}

export const useEstoqueEventosDoCliente = (clienteId: number | null) =>
  useSimples<EventoApto[]>(clienteId ? urlEstoque('getEstoqueEventosDoCliente', { clienteId }) : null, [])

/** Só consulta com 2+ caracteres, como o backend. */
export const useEstoqueTecnicos = (busca: string) => {
  const termo = useDebounce(busca.trim(), 300)
  return { ...useSimples<TecnicoOption[]>(termo.length >= 2 ? urlEstoque('getEstoqueTecnicos', { busca: termo }) : null, []), termo }
}

/**
 * Catálogo do solicitante (ESL1 ou ESL2): todos os itens ativos, sem página
 * e sem posição de estoque. `getEstoqueItens` é de ESL1 e devolve saldo.
 */
const SEM_ITENS: CatalogoItemRow[] = []

export const useEstoqueItensSolicitacao = () =>
  useSimples<CatalogoItemRow[]>(urlEstoque('getEstoqueItensSolicitacao'), SEM_ITENS)

export const useEstoqueMinhaSolicitacao = (id: number | null) =>
  useSimples<MinhaSolicitacaoDetalhe | null>(id ? urlEstoque('getEstoqueMinhaSolicitacaoDetalhe', { id }) : null, null)

// ---- Detalhe do pedido para a logística ----
export const useEstoqueSolicitacaoDetalhe = (id: number | null) =>
  useSimples<SolicitacaoDetalhe | null>(id ? urlEstoque('getEstoqueSolicitacaoDetalhe', { id }) : null, null)

/** Remessas de um pedido, paginadas: a rota devolve 25 por página. */
export const useEstoqueRemessasDoPedido = (solicitacaoId: number | null, page = 1) =>
  useLista<RemessaRow>(solicitacaoId ? urlEstoque('getEstoqueRemessas', { solicitacaoId, page }) : null, lerPaginado)

/** Peças disponíveis para separar (ESL3 ou ESL5); chave nula sem item/local. */
/**
 * Peças disponíveis de um item num local. Pede um item a mais do que devolve
 * em `pecas`, para `cortou` dizer que há mais; a busca espera 300ms parada.
 */
export const useEstoqueDisponiveis = (itemId: number | null, localId: number | null, limite = 50, busca = '', para?: 'TRANSFERENCIA') => {
  const termo = useDebounce(busca.trim(), 300)
  const q = useSimples<DisponivelRow[]>(
    itemId && localId ? urlEstoque('getEstoqueDisponiveis', { itemId, localId, limite: limite + 1, busca: termo || undefined, para }) : null,
    [],
  )
  return { ...q, pecas: q.data.slice(0, limite), cortou: q.data.length > limite, termo }
}

/** Onde o item está livre, por praça (peça ou consumível); chave nula sem item. */
export const useEstoqueOndeTem = (itemId: number | null) =>
  useSimples<OndeTemRow[]>(itemId ? urlEstoque('getEstoqueOndeTem', { itemId }) : null, [])

export const useEstoqueVinculosDoDetentor = (detentorLocalId: number | null) =>
  useLista<VinculoResolvido>(detentorLocalId ? urlEstoque('getEstoqueVinculos', { detentorLocalId, status: 'VIGENTE' }) : null, lerPaginado)

// ---- Remessas ----
export const useEstoqueRemessas = (f: RemessasFilters) =>
  useLista<RemessaRow>(urlEstoque('getEstoqueRemessas', f), lerPaginado)

export const useEstoqueRemessaDetalhe = (id: number | null) =>
  useSimples<RemessaDetalhe | null>(id ? urlEstoque('getEstoqueRemessas', { id }) : null, null)

// ---- Vínculos (contratos e termos) ----
export const useEstoqueVinculos = (f: VinculosFilters) =>
  useLista<VinculoRow>(urlEstoque('getEstoqueVinculos', f), lerPaginado)

export const useEstoqueVinculoDetalhe = (id: number | null) =>
  useSimples<VinculoDetalhe | null>(id ? urlEstoque('getEstoqueVinculos', { id }) : null, null)

/**
 * Remessas de um vínculo, por BLOCO e com página própria.
 *
 * O servidor separa abertas de fechadas (`abertas=1|0`) porque derivar isso de
 * uma página de 25 ordenada por id mentia: 25 encerradas recentes escondiam a
 * antiga ainda aberta e a tela anunciava "nada fora por este contrato".
 */
export const useEstoqueRemessasDoVinculo = (vinculoId: number | null, abertas: '1' | '0', page = 1) =>
  useLista<RemessaRow>(vinculoId ? urlEstoque('getEstoqueRemessas', { vinculoId, abertas, page }) : null, lerPaginado)

/**
 * Anexos já gravados de uma referência (ESL1 para ler).
 *
 * A tela usa para apontar um comprovante que outra pessoa enviou: enviar exige
 * ESL18, expedir exige ESL3, e os dois papéis podem ser pessoas diferentes.
 */
export const useEstoqueAnexos = (refTipo: string, refId: number | null, escopo?: string) =>
  useSimples<AnexoSalvo[]>(refId ? urlEstoque('getEstoqueAnexos', { refTipo, refId, escopo }) : null, [])

// ---- Equipamentos (leitura ESL1) ----

export const BUSCA_MINIMA_EQUIPAMENTOS = 2

export const useEstoqueEquipamentos = (f: EquipamentosFilters) => {
  const termo = useDebounce(f.busca.trim(), 300)
  const busca = termo.length >= BUSCA_MINIMA_EQUIPAMENTOS ? termo : undefined
  const url = f.localId == null ? null : urlEstoque('getEstoqueEquipamentos', { localId: f.localId, itemId: f.itemId, busca, page: f.page })
  return { ...useLista<EquipamentoRow>(url, lerPaginado), termo: busca ?? '' }
}

/** Peças do item na praça; sem item ou sem praça não consulta. */
export const useEstoqueUnidadesDoItem = (itemId: number | null, localId: number | null) =>
  useSimples<UnidadeRow[]>(itemId && localId ? urlEstoque('getEstoqueUnidadesDoItem', { itemId, localId }) : null, [])

/** A peça: é o ESTADO dela que decide o que a tela oferece. */
export const useEstoqueUnidade = (unidadeId: number | null) =>
  useSimples<UnidadeDetalhe | null>(unidadeId ? urlEstoque('getEstoqueUnidade', { unidadeId }) : null, null)

export const useEstoqueHistoricoUnidade = (unidadeId: number | null) =>
  useSimples<HistoricoUnidadeRow[]>(unidadeId ? urlEstoque('getEstoqueHistoricoUnidade', { unidadeId }) : null, [])

/** O que a venda testemunha. Leitura separada: pode faltar sem quebrar a tela. */
export const useEstoqueUsoUnidade = (unidadeId: number | null) =>
  useSimples<UsoUnidade | null>(unidadeId ? urlEstoque('getEstoqueUsoUnidade', { unidadeId }) : null, null)

// ---- Transferências (leitura ESL1) ----
export const useEstoqueTransferencias = (f: TransferenciasFilters) =>
  useLista<TransferenciaRow>(urlEstoque('getEstoqueTransferencias', f), lerPaginado)

/** A lista não traz itens: o detalhe é a única leitura que os devolve. */
export const useEstoqueTransferencia = (id: number | null) =>
  useSimples<TransferenciaDetalhe | null>(id ? urlEstoque('getEstoqueTransferencias', { id }) : null, null)

// ---- Inventário ----
export const useEstoqueInventarios = (f: InventariosFilters) =>
  useLista<InventarioRow>(urlEstoque('getEstoqueInventarios', f), lerPaginado)

export const useEstoqueInventario = (id: number | null) =>
  useSimples<InventarioDetalhe | null>(id ? urlEstoque('getEstoqueInventarioDetalhe', { id }) : null, null)

// ---- Baixas ----
export const useEstoqueBaixas = (f: BaixasFilters) =>
  useLista<BaixaRow>(urlEstoque('getEstoqueBaixas', f), lerPaginado)

/** Peças extraviadas ou sucateadas sem proposta de baixa (lista única, sem página). */
export const useEstoqueBaixasSemProposta = () =>
  useLista<BaixaSemPropostaRow>(urlEstoque('getEstoqueBaixas', { semProposta: '1' }), lerPaginado)

// ---- Catálogo (sem escopo de praça: o catálogo é global) ----
export const useEstoqueCatalogo = (f: CatalogoFilters) =>
  useLista<ItemCatalogoRow>(urlEstoque('getEstoqueItens', f), lerPaginado)

export const useEstoqueItemDetalhe = (id: number | null) =>
  useSimples<ItemCatalogoDetalhe | null>(id ? urlEstoque('getEstoqueItens', { id }) : null, null)

/**
 * Ocorrências: lista única, NÃO paginada, com duas formas de linha —
 * a gravada (tem peça e id) e a derivada do pedido (`id: null`).
 */
export const useEstoqueOcorrencias = (natureza?: NaturezaOcorrencia | '') =>
  useSimples<Ocorrencia[]>(urlEstoque('getEstoqueOcorrencias', natureza ? { natureza } : undefined), [])

/**
 * Categorias do catálogo: é o seletor de quem cadastra item.
 *
 * `habilitado` porque o seletor vive dentro do bloco de ESL17 — quem só
 * visualiza nunca o vê, e não deve pagar a consulta. Mesmo padrão de
 * `useEstoqueEventosDoCliente` e `useEstoqueTecnicos`.
 */
export const useEstoqueCategorias = (habilitado = true) =>
  useSimples<CategoriaRow[]>(habilitado ? urlEstoque('getEstoqueCategorias') : null, [])

export const useEstoqueNotificacaoConfig = () =>
  useSimples<ConfigNotificacao | null>(urlEstoque('getEstoqueNotificacaoConfig'), null)
